import WebViewer, { type WebViewerInstance } from "@pdftron/webviewer";
import { type CSSProperties, useEffect, useMemo, useRef, useState } from "react";

import type { PartialFileUpload } from "../utils";
import { Badge, type BadgeProps } from "./badge";
import { Loading } from "./loading";

export type PdfViewerFile = PartialFileUpload;

interface SourceDocument {
  fileUploadId: string;
  filename: string;
  mimeType: string;
}

interface BoundingBox {
  page: number;
  text: string;
  confidence?: number;
  coordinates: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  sourceDocument: {
    fileUploadId: string;
    filename: string;
  };
}

export interface ExtractedResult {
  boundingBox: BoundingBox;
  sourceDocument: SourceDocument;
  value: string;
  key: string;
}

const getHighlightColorByConfidence = (confidence?: number): { r: number; g: number; b: number; a: number } => {
  const baseColor = { r: 255, g: 255, b: 0, a: 0.6 };

  if (!confidence) {
    return baseColor;
  }

  if (confidence >= 0.9) {
    return { r: 0, g: 255, b: 0, a: 0.6 };
  } else if (confidence >= 0.7) {
    return { r: 255, g: 255, b: 0, a: 0.6 };
  } else {
    return { r: 255, g: 100, b: 100, a: 0.6 };
  }
};

const getConfidenceBadgeVariant = (confidence: number): BadgeProps["variant"] => {
  if (confidence >= 0.9) {
    return "green";
  }

  if (confidence >= 0.7) {
    return "amber";
  }

  return "orange";
};

type WebViewerCore = WebViewerInstance["Core"];
type HighlightAnnotation = InstanceType<WebViewerCore["Annotations"]["TextHighlightAnnotation"]>;

/**
 * Draws a highlight over `searchText` on `pageNumber` and returns the annotation it created, or
 * `null` when the text can't be located. The caller owns the returned annotations so it can delete
 * them again when the extracted results change.
 */
const highlightTextOnPage = async (
  searchText: string,
  pageNumber: number,
  annotationManager: WebViewerCore["annotationManager"],
  Annotations: WebViewerCore["Annotations"],
  documentViewer: WebViewerCore["documentViewer"],
  bbox: BoundingBox
): Promise<HighlightAnnotation | null> => {
  const pageText = await documentViewer.getDocument().loadPageText(pageNumber);

  if (!pageText) {
    return null;
  }

  const cleanSearchText = searchText.replaceAll(/\s+/g, " ").trim();
  const cleanPageText = pageText.replaceAll(/\s+/g, " ");

  let searchIndex = cleanPageText.toLowerCase().indexOf(cleanSearchText.toLowerCase());

  if (searchIndex === -1) {
    searchIndex = pageText.toLowerCase().indexOf(searchText.toLowerCase());
  }

  if (searchIndex === -1) {
    const words = cleanSearchText.split(" ").filter((word) => word.length > 0);
    if (words.length > 1) {
      const primaryWord = words.find((word) => word.length > 2) || words[0];
      searchIndex = cleanPageText.toLowerCase().indexOf(primaryWord.toLowerCase());
    }
  }

  if (searchIndex === -1) {
    return null;
  }

  const quads = await documentViewer
    .getDocument()
    .getTextPosition(pageNumber, searchIndex, searchIndex + cleanSearchText.length);

  if (!quads || quads.length === 0) {
    return null;
  }

  const finalColor = getHighlightColorByConfidence(bbox.confidence);

  const highlightAnnotation = new Annotations.TextHighlightAnnotation({
    PageNumber: pageNumber,
    Quads: quads,
    StrokeColor: new Annotations.Color(finalColor.r, finalColor.g, finalColor.b, finalColor.a),
    Author: "System",
    Subject: `Text: ${searchText}`,
    Contents: `Highlighted text: ${searchText}${bbox.confidence ? ` (confidence: ${bbox.confidence})` : ""}`,
  });

  annotationManager.addAnnotation(highlightAnnotation);
  void annotationManager.drawAnnotationsFromList([highlightAnnotation]);

  return highlightAnnotation;
};

const addBoundingBoxAnnotations = async (
  extractedResults: ExtractedResult[],
  annotationManager: WebViewerCore["annotationManager"],
  Annotations: WebViewerCore["Annotations"],
  documentViewer: WebViewerCore["documentViewer"],
  isActive: () => boolean
) => {
  const created: HighlightAnnotation[] = [];

  for (const result of extractedResults) {
    if (!isActive()) {
      break;
    }

    const annotation = await highlightTextOnPage(
      result.value,
      result.boundingBox.page,
      annotationManager,
      Annotations,
      documentViewer,
      result.boundingBox
    );

    if (annotation) {
      created.push(annotation);
    }
  }

  return created;
};

export const ViewPdfWebViewer = ({
  headers,
  file,
  style,
  extractedResults,
}: {
  headers: HeadersInit;
  file: PdfViewerFile;
  style?: CSSProperties;
  extractedResults: ExtractedResult[];
}) => {
  const viewer = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The viewer with its document already loaded — `null` until it is safe to read from or draw on.
  // Holding it as state rather than a ref is what lets the annotation effect below depend on
  // readiness directly instead of inferring it from a loading flag.
  const [loadedInstance, setLoadedInstance] = useState<WebViewerInstance | null>(null);

  // Callers build `headers` inline (`getAuthRequestHeaders()` returns a fresh object literal), so it
  // gets a new identity on every render of the parent. Depending on it would restart initialization
  // on every ambient re-render of the surrounding tree, tearing down the viewer mid-load. Read it
  // through a ref instead — it's only consumed once, when the fetch starts.
  const headersRef = useRef(headers);
  headersRef.current = headers;

  // Every run's construction and disposal queues onto this chain, so at most one of them is ever
  // touching the `viewer` container. Both halves have to be serialized: `WebViewer()` builds into
  // the container asynchronously, and `UI.dispose()` asynchronously tears that DOM back down.
  const teardownRef = useRef<Promise<unknown>>(Promise.resolve());

  const isLoading = !loadedInstance && !error;

  const navigateToPage = (pageNumber: number) => {
    loadedInstance?.Core.documentViewer.setCurrentPage(pageNumber, false);
  };

  const fileExtractedResults = useMemo(
    () => extractedResults.filter((result) => result.sourceDocument?.fileUploadId === file.id),
    [extractedResults, file.id]
  );

  useEffect(() => {
    // Clear the previous file's outcome before anything can bail out, so a new file never inherits
    // a stale error or the instance belonging to the file we're navigating away from.
    setError(null);
    setLoadedInstance(null);

    const container = viewer.current;

    if (!container) {
      return;
    }

    let isActive = true;
    // Scoped to this effect run rather than a ref, so the cleanup below disposes exactly the
    // instance this run created and nothing a later run owns.
    let instance: WebViewerInstance | null = null;

    // Builds this run's viewer into the shared container. Only ever invoked from the `teardownRef`
    // chain, so it is guaranteed to start after every earlier run has finished both constructing
    // and disposing.
    const construct = async (): Promise<WebViewerInstance | null> => {
      if (!isActive) {
        return null;
      }

      const created = await WebViewer(
        {
          path: "/lib/webviewer",
          licenseKey:
            process.env.REACT_APP_WEBVIEWER_LICENSE_KEY ||
            "demo:1733929272331:7ed6fa850300000000a08edee2912b61e424594747b1b820b9eaf77013",
          fullAPI: true,
          enableRedaction: false,
          enableMeasurement: false,
          enableFilePicker: false,
          isReadOnly: true,
          disabledElements: [
            "toolbarGroup-Edit",
            "toolbarGroup-View",
            "toolbarGroup-Insert",
            "toolbarGroup-Tools",
            "toolbarGroup-Annotate",
            "toolbar",
            "default-ribbon-group",
            "ribbons",
            "toolbarGroup-View",
            "toolbarGroup-Insert",
            "viewTabButton",
            "annotateTabButton",
            "shapesTabButton",
            "insertTabButton",
            "editTabButton",
            "fillAndSignTabButton",
            "formsTabButton",
            "tabsHeader",
            "header",
            "toolsHeader",
            "toolbar",
            "defaultToolbar",
            "annotationToolbar",
            "printButton",
            "saveAsButton",
            "leftPanel",
            "leftPanelButton",
            "notesPanelButton",
            "notesPanel",
            "thumbnailsPanelButton",
            "outlinesPanelButton",
            "bookmarksPanelButton",
            "layersPanelButton",
            "signaturePanelButton",
            "attachmentPanelButton",
            "tabsPanelButton",
            "rubberStampPanelButton",
            "annotationPopup",
            "contextMenuPopup",
            "richTextPopup",
            "textPopup",
            "viewControlsOverlay",
            "menuOverlay",
            "toolsOverlay",
            "signatureOverlay",
            "zoomOverlay",
            "redactionPanel",
            "watermarkPanel",
            "toolsButton",
            "panToolButton",
            "selectToolButton",
            "freeHandToolButton",
            "highlightToolButton",
            "underlineToolButton",
            "strikeoutToolButton",
            "squigglyToolButton",
            "caretToolButton",
            "freeTextToolButton",
            "stickyToolButton",
            "calloutToolButton",
            "rectangleToolButton",
            "ellipseToolButton",
            "polygonToolButton",
            "cloudToolButton",
            "polylineToolButton",
            "lineToolButton",
            "arrowToolButton",
            "pencilToolButton",
            "eraserToolButton",
            "signatureToolButton",
            "stampToolButton",
            "rubberStampToolButton",
            "fileAttachmentToolButton",
            "soundToolButton",
            "distanceMeasurementToolButton",
            "perimeterMeasurementToolButton",
            "areaMeasurementToolButton",
            "rectangularAreaMeasurementToolButton",
            "cloudyRectangularAreaMeasurementToolButton",
            "countMeasurementToolButton",
            "redactionToolButton",
            "contentEditToolButton",
            "addParagraphToolButton",
            "addImageContentToolButton",
            "cropToolButton",
            "insertPageButton",
            "replacePageButton",
            "deletePageButton",
            "rotateClockwiseButton",
            "rotateCounterClockwiseButton",
            "pageTransitionButton",
            "fullscreenButton",
            "settingsButton",
            "languageButton",
            "helpButton",
            "accessibilityButton",
          ],
        },
        container
      );

      // A teardown that lands while `WebViewer` was still constructing leaves an instance the
      // cleanup has already run past. Dispose it here instead of publishing it — awaited, so the
      // next run in the chain does not start against a container still being dismantled.
      if (!isActive) {
        await created.UI.dispose?.();
        return null;
      }

      instance = created;

      return created;
    };

    const initializeWebViewer = async () => {
      const url = new URL(`/api/files/${file.id}`, globalThis.location.origin);

      const response = await fetch(url.toString(), { headers: headersRef.current });
      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.statusText}`);
      }
      const arrayBuffer = await response.arrayBuffer();

      if (!isActive) {
        return;
      }

      // Claim the serialization slot and publish this run's *construction* as what the next run
      // waits on. Waiting only on the disposal of already-published instances isn't enough: a run
      // cancelled while `WebViewer()` is still building has no instance to hand the cleanup, so the
      // next run would start a second construction into the same container and the first run's late
      // `dispose()` would then tear down its successor's viewer.
      const construction = teardownRef.current.then(construct);
      teardownRef.current = construction.catch(() => undefined);

      const created = await construction;

      if (!created) {
        return;
      }

      await created.Core.documentViewer.loadDocument(arrayBuffer, {
        filename: file.filename,
      });

      if (!isActive) {
        return;
      }

      setLoadedInstance(created);
    };

    void initializeWebViewer().catch((initError: unknown) => {
      // A cancelled run is a deliberate teardown, not a failure the user should see.
      if (!isActive) {
        return;
      }

      setError(initError instanceof Error ? initError.message : "Failed to load PDF");
    });

    return () => {
      isActive = false;

      const disposing = instance;
      instance = null;

      if (disposing) {
        teardownRef.current = teardownRef.current.then(() => disposing.UI.dispose?.()).catch(() => undefined);
      }
    };
  }, [file.id, file.filename]);

  // Annotations are applied to the loaded instance rather than from inside the initialization effect
  // above, so a change to the extracted results re-highlights instead of rebooting WebViewer.
  useEffect(() => {
    if (!loadedInstance || fileExtractedResults.length === 0) {
      return;
    }

    let isActive = true;
    let created: HighlightAnnotation[] = [];
    const { documentViewer, annotationManager, Annotations } = loadedInstance.Core;

    void addBoundingBoxAnnotations(
      fileExtractedResults,
      annotationManager,
      Annotations,
      documentViewer,
      () => isActive
    ).then((annotations) => {
      created = annotations;

      if (!isActive && annotations.length > 0) {
        annotationManager.deleteAnnotations(annotations);
      }
    });

    return () => {
      isActive = false;

      if (created.length > 0) {
        annotationManager.deleteAnnotations(created);
      }
    };
  }, [loadedInstance, fileExtractedResults]);

  return (
    <div
      style={{
        width: "100%",
        height: "100dvh",
        position: "relative",
        display: "flex",
        gap: "1rem",
        ...style,
      }}
    >
      <div className="flex flex-col gap-2 max-w-1/4 overflow-y-auto">
        {fileExtractedResults.map(({ boundingBox, value }, index) => (
          <div
            key={index}
            className="p-3 max-h-min bg-white rounded-md shadow-sm border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => navigateToPage(boundingBox.page)}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <span className="text-xs text-gray-500">Page {boundingBox.page}</span>
              {boundingBox.confidence && (
                <Badge variant={getConfidenceBadgeVariant(boundingBox.confidence)} size="2xs">
                  {Math.round(boundingBox.confidence * 100)}%
                </Badge>
              )}
            </div>
            <p className="text-sm text-gray-900 line-clamp-3 text-ellipsis whitespace-nowrap">{value}</p>
          </div>
        ))}
      </div>

      <div
        style={{
          flexBasis: "75%",
          flex: 1,
          position: "relative",
          transition: "width 0.3s ease",
          borderRadius: "0.5rem",
          overflow: "hidden",
        }}
      >
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
            <Loading label="Loading PDF..." />
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
            <div className="text-red-600 text-center">
              <p className="font-medium">Error loading PDF</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}
        {/* Always mounted, never behind a conditional return: the init effect needs this container
            to still be in the DOM to recover when the file changes after a failed load. */}
        <div
          ref={viewer}
          style={{
            width: "100%",
            height: "100%",
          }}
        />
      </div>
    </div>
  );
};
